<script setup lang="ts">
import { ramadanRehnumai } from "@/data/ramadan/rehnumai";
import type { RamadanFocus, RamadanMode } from "@/data/ramadan/types";

const route = useRoute();
const router = useRouter();

const mode = ref<RamadanMode>("iftar");
const focus = ref<RamadanFocus>("health");

const focusPanels: Array<{
  key: RamadanFocus;
  label: string;
  icon: string;
  description: string;
}> = [
  {
    key: "health",
    label: "Sehat",
    icon: "H",
    description: "ghizai mashwaray aur sehat ka focus",
  },
  {
    key: "sunnah",
    label: "Sunnah",
    icon: "S",
    description: "masnoon tareeqe aur adab",
  },
];

function getModeFromQuery(value: unknown): RamadanMode | null {
  return value === "iftar" || value === "sehri" ? value : null;
}

function getFocusFromQuery(value: unknown): RamadanFocus | null {
  return value === "health" || value === "sunnah" ? value : null;
}

function getItemDescription(item: { description?: string; explanation?: string }) {
  return item.description ?? item.explanation ?? "";
}

onMounted(() => {
  const queryMode = Array.isArray(route.query.mode)
    ? route.query.mode[0]
    : route.query.mode;
  const queryFocus = Array.isArray(route.query.focus)
    ? route.query.focus[0]
    : route.query.focus;

  const parsedMode = getModeFromQuery(queryMode);
  const parsedFocus = getFocusFromQuery(queryFocus);

  if (parsedMode) mode.value = parsedMode;
  if (parsedFocus) focus.value = parsedFocus;
});

watch([mode, focus], () => {
  router.replace({
    query: {
      ...route.query,
      mode: mode.value,
      focus: focus.value,
    },
  });
});

const content = computed(() => ramadanRehnumai[mode.value][focus.value]);
const modeLabel = computed(() => (mode.value === "iftar" ? "Iftar" : "Sehri"));
const focusLabel = computed(() =>
  focus.value === "health" ? "Sehat" : "Sunnah",
);

useSeoMeta({
  title: computed(
    () =>
      `${mode.value === "iftar" ? "Iftar" : "Sehri"} Rehnumai: ${
        focus.value === "health" ? "Sehat Guide" : "Sunnah Guide"
      } | Kya Karain, Kis Se Parhez Karein`,
  ),
  description:
    "Ramadan mein Sehri aur Iftar ke liye mukammal rehnumai. Janiye kya khana chahiye aur kya kam karna chahiye, sehat aur sunnah ke sath.",
});

watchEffect(() => {
  useHead({
    script: [
      {
        key: "rehnumai-howto",
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: `${mode.value} guide (${focus.value})`,
          step: content.value.do.map((item, index) => ({
            "@type": "HowToStep",
            position: index + 1,
            name: item.title,
            text: getItemDescription(item),
          })),
        }),
      },
    ],
  });
});
</script>

<template>
  <div
    class="max-w-2xl mx-auto px-4 py-6 md:py-8 text-gray-900 dark:text-gray-100"
  >
    <nav
      aria-label="Breadcrumb"
      class="mb-4 flex flex-wrap items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
    >
      <span class="text-gray-700 dark:text-gray-200">Ramadan</span>
      <span>&gt;</span>
      <span class="text-gray-700 dark:text-gray-200">{{ modeLabel }}</span>
      <span>&gt;</span>
      <span class="text-gray-700 dark:text-gray-200">{{ focusLabel }}</span>
    </nav>

    <div class="text-left mb-8">
      <h1
        class="text-2xl md:text-3xl font-light text-gray-900 dark:text-gray-100 mb-2 tracking-tight"
      >
        Sehri o Iftar Rehnumai
      </h1>
      <p class="text-sm text-gray-500 dark:text-gray-400 font-light">
        sehat aur sunnah ke mutabiq kya karain aur kis se parhez karein
      </p>
    </div>

    <section
      class="mb-8 rounded-2xl border border-gray-200 bg-white/80 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900/70"
    >
      <div class="mb-3">
        <div
          class="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-gray-100 dark:bg-slate-800/80"
        >
          <button
            class="py-3 text-sm rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70"
            :class="
              mode === 'iftar'
                ? 'bg-white text-gray-900 shadow-sm dark:bg-slate-700 dark:text-gray-100'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            "
            @click="mode = 'iftar'"
            :aria-pressed="mode === 'iftar'"
            type="button"
          >
            Iftar
          </button>

          <button
            class="py-3 text-sm rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70"
            :class="
              mode === 'sehri'
                ? 'bg-white text-gray-900 shadow-sm dark:bg-slate-700 dark:text-gray-100'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            "
            @click="mode = 'sehri'"
            :aria-pressed="mode === 'sehri'"
            type="button"
          >
            Sehri
          </button>
        </div>
      </div>

      <div class="space-y-2" role="tablist" aria-label="Focus tabs">
        <button
          v-for="panel in focusPanels"
          :key="panel.key"
          class="w-full rounded-xl border text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70"
          :class="
            focus === panel.key
              ? 'border-emerald-300 bg-emerald-50/80 dark:border-emerald-700 dark:bg-emerald-950/30'
              : 'border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900'
          "
          :aria-expanded="focus === panel.key"
          :aria-selected="focus === panel.key"
          :aria-controls="`panel-${panel.key}`"
          role="tab"
          type="button"
          @click="focus = panel.key"
        >
          <div class="flex items-center justify-between px-4 py-3">
            <div class="flex items-center gap-2">
              <span
                class="inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs"
                :class="
                  focus === panel.key
                    ? 'border-emerald-400 text-emerald-700 dark:border-emerald-600 dark:text-emerald-300'
                    : 'border-gray-300 text-gray-500 dark:border-slate-600 dark:text-gray-400'
                "
              >
                {{ panel.icon }}
              </span>
              <div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {{ panel.label }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ panel.description }}
                </p>
              </div>
            </div>
            <span
              class="text-xs text-gray-500 dark:text-gray-400 transition-transform"
              :class="focus === panel.key ? 'rotate-180' : ''"
            >
              v
            </span>
          </div>
        </button>
      </div>
    </section>

    <Transition
      mode="out-in"
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div :key="mode + '-' + focus" :id="`panel-${focus}`" role="tabpanel">
        <section
          class="mb-10 rounded-2xl border border-emerald-200/70 bg-emerald-50/30 p-4 dark:border-emerald-900 dark:bg-emerald-950/20"
        >
          <div class="flex items-center gap-2 mb-6">
            <span class="text-emerald-600 dark:text-emerald-400 text-lg">+</span>
            <h2
              class="text-sm font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider"
            >
              Kya Karain
            </h2>
          </div>

          <div class="space-y-4">
            <article
              v-for="item in content.do"
              :key="item.id"
              class="border-b border-emerald-100 pb-4 last:border-0 dark:border-emerald-900/70"
            >
              <h3
                class="text-sm font-medium text-gray-800 dark:text-gray-100 mb-1 leading-relaxed"
              >
                {{ item.title }}
              </h3>
              <p class="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                {{ getItemDescription(item) }}
              </p>
            </article>
          </div>
        </section>

        <section
          v-if="content.avoid.length"
          class="mb-10 rounded-2xl border border-amber-200/70 bg-amber-50/30 p-4 dark:border-amber-900/70 dark:bg-amber-950/20"
        >
          <div class="flex items-center gap-2 mb-6">
            <span class="text-amber-500 dark:text-amber-400 text-lg">!</span>
            <h2
              class="text-sm font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider"
            >
              Kis Se Parhez Karein
            </h2>
          </div>

          <div class="space-y-4">
            <article
              v-for="item in content.avoid"
              :key="item.id"
              class="border-b border-amber-100 pb-4 last:border-0 dark:border-amber-900/70"
            >
              <h3
                class="text-sm font-medium text-gray-800 dark:text-gray-100 mb-1 leading-relaxed"
              >
                {{ item.title }}
              </h3>
              <p class="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                {{ getItemDescription(item) }}
              </p>
            </article>
          </div>
        </section>

        <p
          v-if="mode === 'sehri' && focus === 'health'"
          class="mb-8 text-sm text-gray-600 dark:text-gray-300"
        >
          Busy logon ke liye Sehri food ideas
          <NuxtLink
            to="/blog/sehri-mein-kya-khayein-ramzan-2026/"
            class="underline underline-offset-2"
          >
            Sehri Food Ideas for Busy Professionals
          </NuxtLink>
        </p>
      </div>
    </Transition>

    <section class="mb-8 text-sm text-gray-600 dark:text-gray-300">
      <h2 class="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
        📖 Mazeed rehnumai
      </h2>
      <ul class="space-y-1">
        <li>
          <NuxtLink
            to="/blog/sehri-uthne-ka-tareeqa-ramzan-2026/"
            class="underline underline-offset-2"
          >
            How to Wake Up for Sehri Every Day
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            to="/blog/sehri-mein-kya-khayein-ramzan-2026/"
            class="underline underline-offset-2"
          >
            Sehri Food Ideas for Busy Professionals
          </NuxtLink>
        </li>
      </ul>
    </section>

    <hr class="border-t border-gray-200 dark:border-slate-700 my-8" />

    <div class="text-center">
      <p class="text-[11px] text-gray-500 dark:text-gray-400 font-light">
        yeh rehnumai hai, medical advice nahi
      </p>
    </div>
  </div>
</template>

<style scoped>
button {
  transition: all 0.2s ease;
}

button:focus-visible {
  outline: 2px solid #10b981;
  outline-offset: 2px;
}
</style>
